import { Suspense, lazy, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthToken } from "@convex-dev/auth/react";

import { ChatWindow } from "@/components/chat/ChatWindow";
import { WorkflowPending } from "@/components/workflows/WorkflowPending";
import { WORKFLOW_CHAT_STARTERS } from "@/components/workflows/workflowChatStarters";
import { useSpec } from "@/hooks/useSpec";
import { buildSpecSlug } from "@domain/praxis-specs";
import type { Id } from "@convex/_generated/dataModel";

// Thread-keyed SpecTab: its empty state (thread owns no spec yet) is the
// SOP drop zone / paste input — the PDF/.txt creation path lives here.
const SpecTab = lazy(() =>
  import("@/components/tasks/SpecTab").then((m) => ({ default: m.SpecTab })),
);

export const Route = createFileRoute("/_authenticated/workflows/new/$threadId")({
  component: WorkflowGestationRoute,
  validateSearch: (search: Record<string, unknown>): { seed?: string } => {
    // Optional composer prefill from the library's "Describe a new job" —
    // pre-filled ONCE for the user to review + send manually, never
    // auto-sent (same contract as the chat route's `seed`).
    return typeof search.seed === "string" ? { seed: search.seed } : {};
  },
});

function SpecTabLoading() {
  return (
    <div className="flex h-full items-center justify-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
      Loading…
    </div>
  );
}

/**
 * The new-workflow gestation route — the ONLY thread-keyed workflows URL.
 * A workflow's `specs` row is born from the projection 10–30s after the
 * agent writes the spec, so a fresh workflow can only be addressed by its
 * thread. This route watches `specs.getForThread` and replace-redirects to
 * the spec-keyed detail route (`/workflows/$slug`) the moment the row
 * lands; until then the user describes the job in the embedded chat or
 * drops a doc on SpecTab's empty state.
 */
function WorkflowGestationRoute() {
  const { threadId } = Route.useParams();
  const { seed } = Route.useSearch();
  const navigate = useNavigate();
  const authToken = useAuthToken();
  const { spec } = useSpec({ threadId: threadId as Id<"threads"> });

  // The spec landed → this workflow now has a real identity; hand off to
  // the canonical slug route.
  useEffect(() => {
    if (spec === null) return;
    void navigate({
      to: "/workflows/$slug",
      params: { slug: buildSpecSlug({ title: spec.title, specId: spec._id }) },
      search: { tab: "overview" },
      replace: true,
    });
  }, [spec, navigate]);

  // Strip the one-shot `seed` param once ChatWindow has captured it into
  // its draft state (consume-then-strip, frontend-doctrine §4.11).
  useEffect(() => {
    if (seed === undefined || spec !== null) return;
    void navigate({
      to: "/workflows/new/$threadId",
      params: { threadId },
      search: {},
      replace: true,
    });
  }, [seed, spec, threadId, navigate]);

  return (
    <WorkflowPending
      onBack={() => void navigate({ to: "/workflows" })}
      chatSlot={
        <ChatWindow
          className="flex-1 overflow-hidden"
          threadId={threadId as Id<"threads">}
          authToken={authToken}
          initialDraft={seed}
          starterSet={WORKFLOW_CHAT_STARTERS}
        />
      }
      specSlot={
        <Suspense fallback={<SpecTabLoading />}>
          <SpecTab threadId={threadId as Id<"threads">} />
        </Suspense>
      }
    />
  );
}
